import { Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface TestimonialCardProps {
  quote: string;
  name: string;
  title: string;
  rating: number;
  service: string;
  location: string;
}

const TestimonialCard = ({ quote, name, title, rating, service, location }: TestimonialCardProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="bg-white p-8 rounded-xl shadow-enhanced h-full flex flex-col border border-border/50 hover:border-primary/20 transition-all duration-300"
    >
      {/* Animated Star Rating */}
      <div className="flex mb-4 gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-5 h-5 transition-all duration-300 ${
              isVisible && i < Math.floor(rating)
                ? "fill-primary text-primary scale-100 opacity-100"
                : i < Math.floor(rating)
                  ? "fill-primary text-primary"
                  : "fill-muted text-muted"
            }`}
            style={{
              transitionDelay: isVisible ? `${i * 100}ms` : '0ms',
              transform: isVisible && i < Math.floor(rating) ? 'scale(1)' : i >= Math.floor(rating) ? 'scale(1)' : 'scale(0.5)',
              opacity: isVisible || i >= Math.floor(rating) ? 1 : 0
            }}
          />
        ))}
      </div>
      <h3 className="font-heading font-bold text-navy text-lg mb-3">{title}</h3>
      {/* Quote */}
      <p className="text-charcoal leading-relaxed mb-6 flex-grow">
        "{quote}"
      </p>
      <div className="border-t border-border pt-4 mt-auto">
        <p className="font-semibold text-charcoal">{name}</p>
        <p className="text-sm text-muted-foreground">{location}</p>
        <p className="text-sm text-primary/80 mt-1 font-medium">{service}</p>
      </div>
    </div>
  );
};

export default TestimonialCard;
